import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { Website } from "../models";

interface Query<T = unknown> {
    timestamp: number;
    total: number;
    data: T;
}

declare global {
    interface Window {
        website?: Promise<Query<Website>>;
    }
}

interface WebsiteContextType {
    website: Website;
    setWebsite: (account: Website) => void;
}

export const WebsiteContext = createContext<WebsiteContextType | undefined>(undefined);

export const WebsiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [state, setState] = useState<Query<Website>>(() => {
        try {
            const stored = localStorage.getItem("website");
            if (stored) { 
                return JSON.parse(stored);
            }
        } catch (err) {
            console.error("Failed to parse website cache:", err);
        }
        return { timestamp: 0, total: 0, data: { brands: [], categories: [], currency: "USD" } };
    });
    const { data: website } = state;

    const setWebsite = useCallback((w: Website) => {
        const query = { data: w, timestamp: Date.now(), total: 1 } as Query<Website>;
        setState(query);
        localStorage.setItem("website", JSON.stringify(query));
    }, [setState])
    
    useEffect(() => {
        const promise = window.website;
        if (!promise) {
             return;
        }
        promise
            .then((w) => {
                if (w?.data?.accountId) {
                    localStorage.setItem("website", JSON.stringify(w));
                    setState(w);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch website data:", err);
            });
    }, []);

    return (
        <WebsiteContext.Provider value={{ website, setWebsite }}>
            {children}
        </WebsiteContext.Provider>
    );
};

export const useWebsite = () => {
    const context = useContext(WebsiteContext)?.website;
    if (context === undefined) {
        throw new Error('useWebsiteData must be used within a WebsiteProvider');
    }
    return context;
};

